import * as Yup from 'yup';
import {I18n} from '@aws-amplify/core';

import {DIMENSION_KINDS, MAX_DIMENSIONS, PRICE_TERMS} from '../types';

// Mirrors the API rules the form can know about. The server enforces them too;
// this is so the operator hears about it before the round trip.

export const USAGE_UNAVAILABLE_MESSAGE =
    'Usage pricing needs the billing integration, which is not available yet. Use flat or contract pricing.';

// Messages are functions, so I18n.get runs when Yup validates, after the
// app's vocabulary has loaded, not once at import.
const t = (message: string) => () => I18n.get(message);

// Up to 9 integer digits and 3 decimal places, as the API accepts ("49",
// "49.00", "0.001"). See isValidAmount in flexinet-marketplace-api.
const AMOUNT_PATTERN = /^\d{1,9}(\.\d{1,3})?$/;

const priceSchema = Yup.object({
    term: Yup.string()
             .oneOf([...PRICE_TERMS], t('Choose a term'))
             .required(t('Choose a term')),
    amount: Yup.string()
               .trim()
               .required(t('Enter an amount'))
               .matches(AMOUNT_PATTERN, t('Use a decimal with up to 3 places, like 49.00')),
});

const dimensionSchema = Yup.object({
    name: Yup.string().trim().required(t('Enter a name')),
    unit: Yup.string().trim().required(t('Enter a unit')),
    kind: Yup.string()
             .oneOf([...DIMENSION_KINDS], t('Choose a kind'))
             .required(t('Choose a kind'))
             // The kind exists in the API, but a usage unit is refused until the
             // billing integration is there to supply its meter.
             .test('usage-unavailable', t(USAGE_UNAVAILABLE_MESSAGE), value => value !== 'USAGE'),
    prices: Yup.array()
               .of(priceSchema)
               .min(1, t('Add at least one price'))
               .test('one-price-per-term', t('Each term can be priced only once per unit'), prices => {
                   const terms = (prices ?? []).map(price => price?.term).filter(Boolean);
                   return new Set(terms).size === terms.length;
               }),
});

export const RecordSchema = Yup.object({
    name: Yup.string().trim().required(t('Enter a name')),
    summary: Yup.string().trim().required(t('Enter a summary')),
    description: Yup.string().trim().required(t('Enter a description')),
    eulaUrl: Yup.string()
                .trim()
                .required(t('Enter the EULA link'))
                .matches(/^https:\/\/\S+$/, t('The EULA link must start with https://')),
    supportContact: Yup.string().trim().required(t('Enter a support contact')),
    dimensions: Yup.array()
                   .of(dimensionSchema)
                   .min(1, t('Add at least one pricing unit'))
                   .max(MAX_DIMENSIONS, () => `${I18n.get('A product can have at most')} ${MAX_DIMENSIONS} ${I18n.get('pricing units')}`),
});
